import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import * as Icons from 'react-icons/fa';
import { FaChartLine, FaWallet, FaUserFriends, FaEye, FaCalendarAlt, FaCircle, FaClock, FaArrowUp, FaArrowDown, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

// --- ULTRA PREMIUM COLORFUL THEME ---
const THEME = {
  bg: '#f8fafc',          
  card: '#ffffff',        
  primary: '#0f172a',     
  textSub: '#64748b',     
  border: '#e2e8f0',      
  
  // Mesh/Vibrant Gradients for KPI Cards
  gradRevenue: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  glowRevenue: 'rgba(16, 185, 129, 0.4)',
  
  gradLeads: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
  glowLeads: 'rgba(99, 102, 241, 0.4)',
  
  gradOpen: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  glowOpen: 'rgba(139, 92, 246, 0.4)',
  
  gradHealth: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
  glowHealth: 'rgba(15, 23, 42, 0.4)',
  
  chartSent: '#6366f1',
  chartRead: '#10b981',
  chartDelivered: '#f59e0b'
};

// --- ULTRA CUSTOM CSS INJECTIONS ---
const injectStyles = () => {
  if (document.getElementById('dashboard-styles')) return;
  const style = document.createElement('style');
  style.id = 'dashboard-styles';
  style.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
    
    .dash-container { font-family: 'Plus Jakarta Sans', sans-serif; }
    
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes pulseGlow {
      0% { transform: scale(1); opacity: 0.5; }
      50% { transform: scale(1.05); opacity: 0.8; }
      100% { transform: scale(1); opacity: 0.5; }
    }
    
    .animate-up { animation: fadeUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
    .delay-1 { animation-delay: 0.1s; }
    .delay-2 { animation-delay: 0.2s; }
    .delay-3 { animation-delay: 0.3s; }
    
    .colorful-card {
      border-radius: 28px;
      color: white;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
      border: 1px solid rgba(255,255,255,0.1);
    }
    .colorful-card:hover { 
      transform: translateY(-5px); 
      box-shadow: 0 20px 40px -10px var(--hover-glow);
    }
    
    .colorful-card::before {
      content: ''; position: absolute; top: -50%; left: -50%; width: 200%; height: 200%;
      background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%);
      opacity: 0; transition: opacity 0.5s ease; pointer-events: none;
    }
    .colorful-card:hover::before { opacity: 1; }
    
    .white-card {
      background: white;
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.6);
      box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;
    }
    .white-card:hover {
      box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.08);
      border-color: rgba(226, 232, 240, 1);
    }
    
    /* Sleek Segmented Control */
    .segment-btn {
      padding: 10px 20px; border-radius: 12px; border: none; font-size: 13px; font-weight: 700; 
      cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); position: relative;
      background: transparent; color: ${THEME.textSub};
    }
    .segment-btn.active {
      background: ${THEME.primary}; color: white;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
    }
    .segment-btn:not(.active):hover { background: #f1f5f9; color: ${THEME.primary}; }
    
    .feed-item { transition: background 0.2s ease; border-radius: 16px; padding: 12px; margin: 0 -12px; }
    .feed-item:hover { background: #f8fafc; }
    
    .hide-scrollbar::-webkit-scrollbar { display: none; }
    .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
    
    .custom-scroll::-webkit-scrollbar { width: 6px; }
    .custom-scroll::-webkit-scrollbar-track { background: transparent; }
    .custom-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
    .custom-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
  `;
  document.head.appendChild(style);
};

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', padding: '15px 20px', borderRadius: '16px', border: `1px solid ${THEME.border}`, boxShadow: '0 10px 30px -10px rgba(0,0,0,0.15)' }}>
        <p style={{ margin: '0 0 10px 0', fontWeight: '800', fontSize: '13px', color: THEME.primary, borderBottom: `1px solid ${THEME.border}`, paddingBottom: '8px' }}>{label}</p>
        {payload.map((entry, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px', fontSize: '13px', fontWeight: '700', color: THEME.textSub }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: entry.color }}></div>
            <span style={{flex: 1}}>{entry.name}:</span>
            <span style={{color: THEME.primary, fontWeight: '800'}}>{entry.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

function Dashboard() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // --- DATE ENGINE ---
  const [datePreset, setDatePreset] = useState('30days'); 
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // --- ADVANCED ANALYTICS STATE ---
  const [metrics, setMetrics] = useState({ 
    newLeads: 0, totalSent: 0, totalDelivered: 0, totalRead: 0, spend: 0, 
    readRate: 0, costPerLead: 0, estRevenue: 0, healthScore: 0, conversionRate: 0 
  });
  
  const [engagementData, setEngagementData] = useState([]);
  const [leadData, setLeadData] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  const COST_PER_MSG = 0.80; 
  const AVG_SPA_TICKET = 1500; 

  useEffect(() => {
    injectStyles();
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    const unsubscribeAuth = auth.onAuthStateChanged((u) => {
      setUser(u);
      if (u) fetchDashboardData(u.uid);
    });
    
    return () => {
        window.removeEventListener('resize', handleResize);
        unsubscribeAuth();
    }
  }, [datePreset, customStart, customEnd]); 

  // --- DATA PROCESSOR (UNCHANGED LOGIC) ---
  const getActiveDateRange = () => {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    if (datePreset === 'today') {
      start.setHours(0, 0, 0, 0);
    } else if (datePreset === 'yesterday') {
      start.setDate(now.getDate() - 1); start.setHours(0, 0, 0, 0);
      end.setDate(now.getDate() - 1); end.setHours(23, 59, 59, 999);
    } else if (datePreset === '7days') {
      start.setDate(now.getDate() - 7); start.setHours(0, 0, 0, 0);
    } else if (datePreset === '30days') {
      start.setDate(now.getDate() - 30); start.setHours(0, 0, 0, 0);
    } else if (datePreset === 'custom') {
      start = customStart ? new Date(customStart) : new Date(); start.setHours(0, 0, 0, 0);
      end = customEnd ? new Date(customEnd) : new Date(); end.setHours(23, 59, 59, 999);
    }
    return { start, end };
  };

  const fetchDashboardData = (uid) => {
    const { start, end } = getActiveDateRange();
    
    const qContacts = query(collection(db, "businesses", uid, "contacts"), orderBy("lastActive", "desc"));
    const qBroadcasts = query(collection(db, "businesses", uid, "broadcasts"), orderBy("createdAt", "desc"));

    onSnapshot(qContacts, (contactSnap) => {
      const allContacts = contactSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      onSnapshot(qBroadcasts, (broadcastSnap) => {
        const allBroadcasts = broadcastSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        const filteredBroadcasts = allBroadcasts.filter(b => {
          if (!b.createdAt) return false;
          const date = b.createdAt.toDate();
          return date >= start && date <= end;
        });

        const filteredContacts = allContacts.filter(c => {
          if (!c.lastActive) return false;
          const date = c.lastActive.toDate();
          return date >= start && date <= end;
        });

        let tSent = 0, tDelivered = 0, tRead = 0;
        const eDataMap = new Map(); 

        [...filteredBroadcasts].reverse().forEach(camp => {
          tSent += (camp.sent || 0);
          tDelivered += (camp.delivered || 0); 
          tRead += (camp.read || 0);
          
          const dateStr = camp.createdAt ? camp.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Unknown';
          
          if (eDataMap.has(dateStr)) {
              const existing = eDataMap.get(dateStr);
              eDataMap.set(dateStr, { name: dateStr, Sent: existing.Sent + (camp.sent || 0), Read: existing.Read + (camp.read || 0) });
          } else {
              eDataMap.set(dateStr, { name: dateStr, Sent: camp.sent || 0, Read: camp.read || 0 });
          }
        });
        
        const eData = Array.from(eDataMap.values());

        const leadsByDate = {};
        filteredContacts.forEach(c => {
           const dString = c.lastActive.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
           leadsByDate[dString] = (leadsByDate[dString] || 0) + 1;
        });
        const lData = Object.keys(leadsByDate).map(k => ({ date: k, Leads: leadsByDate[k] }));

        const calcReadRate = tSent === 0 ? 0 : Math.round((tRead / tSent) * 100);
        const calcDeliveryRate = tSent === 0 ? 0 : Math.round((tDelivered / tSent) * 100);
        
        const totalSpend = tSent * COST_PER_MSG;
        const totalLeads = filteredContacts.length;
        const cpl = totalLeads > 0 ? (totalSpend / totalLeads) : 0;
        
        const estimatedConversions = Math.floor(totalLeads * 0.15); 
        const convRate = totalLeads > 0 ? ((estimatedConversions / totalLeads) * 100).toFixed(1) : 0;
        const estRevenue = estimatedConversions * AVG_SPA_TICKET;
        
        let hScore = 50 + (calcReadRate * 0.4) + (calcDeliveryRate * 0.2) + (Math.min(totalLeads, 100) * 0.3);
        if (hScore > 100) hScore = 100;
        if (tSent === 0 && totalLeads === 0) hScore = 0; 

        setMetrics({
          newLeads: totalLeads, totalSent: tSent, totalDelivered: tDelivered, totalRead: tRead,
          spend: totalSpend, readRate: calcReadRate, costPerLead: cpl, estRevenue: estRevenue,
          healthScore: Math.round(hScore), conversionRate: convRate
        });
        
        setEngagementData(eData);
        setLeadData(lData);
        
        setFunnelData([
            { name: 'Read', value: tRead },
            { name: 'Delivered', value: tDelivered > tRead ? tDelivered - tRead : 0 },
            { name: 'Sent', value: tSent > tDelivered ? tSent - tDelivered : 0 }
        ]);

        const activity = [];
        filteredContacts.slice(0, 5).forEach(c => {
          activity.push({ id: c.id, type: 'lead', title: `New interaction: ${c.phone}`, time: c.lastActive.toDate(), color: '#3b82f6', icon: <FaUserFriends/> });
        });
        filteredBroadcasts.slice(0, 5).forEach(b => {
          activity.push({ id: b.id, type: 'blast', title: `Campaign sent: ${b.name || 'Unnamed'}`, time: b.createdAt.toDate(), color: '#8b5cf6', icon: <Icons.FaBullhorn/> });
        });
        activity.sort((a, b) => b.time - a.time);
        setRecentActivity(activity.slice(0, 8)); 
      });
    });
  };

  if (!user) return <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', color:THEME.textSub, fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '18px', fontWeight: '800' }}><div style={{width:'24px', height:'24px', border:'3px solid #e2e8f0', borderTopColor: THEME.primary, borderRadius:'50%', animation:'spin 1s linear infinite', marginRight:'10px'}}></div> Initializing Command Center...</div>;

  return (
    <div className="dash-container" style={{ background: THEME.bg, minHeight: '100vh', padding: isMobile ? '20px 15px' : '40px 50px', overflowX: 'hidden' }}>
      
      {/* --- TOP CONTROL BAR --- */}
      <div className="animate-up" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', marginBottom: isMobile ? '30px' : '50px', gap: '20px' }}>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0, fontSize: isMobile ? '32px' : '42px', fontWeight: '900', color: THEME.primary, letterSpacing: '-1.5px', lineHeight: '1.1' }}>Analytics<br/>Command Center</h1>
            {isMobile && (
               <span style={{ background: 'white', color: '#047857', fontSize: '11px', fontWeight: '800', padding: '6px 12px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', border: `1px solid ${THEME.border}` }}>
                 <div style={{width:'8px', height:'8px', borderRadius:'50%', background: '#10b981', animation:'pulseGlow 2s infinite'}}></div> LIVE
               </span>
            )}
          </div>
          <p style={{ margin: '8px 0 0 0', color: THEME.textSub, fontSize: '15px', fontWeight: '600' }}>Deep dive into your broadcast performance.</p>
        </div>

        {/* ULTRA iOS-STYLE SEGMENTED DATE PICKER */}
        <div className="hide-scrollbar" style={{ width: isMobile ? '100%' : 'auto', overflowX: 'auto', paddingBottom: isMobile ? '5px' : '0' }}>
          <div style={{ background: 'white', padding: '6px', borderRadius: '16px', display: 'inline-flex', alignItems: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: `1px solid ${THEME.border}`, whiteSpace: 'nowrap' }}>
            {['today', '7days', '30days'].map(range => (
              <button 
                key={range}
                onClick={() => setDatePreset(range)}
                className={`segment-btn ${datePreset === range ? 'active' : ''}`}
              >
                {range === 'today' ? 'Today' : range === '7days' ? 'Last 7 Days' : 'Last 30 Days'}
              </button>
            ))}
            <div style={{ width: '1px', height: '24px', background: '#e2e8f0', margin: '0 12px' }}></div>
            <div style={{display:'flex', alignItems:'center', padding: '0 16px 0 4px', color:THEME.textSub}}>
                <FaCalendarAlt style={{ marginRight: '8px', fontSize: '14px', color: datePreset === 'custom' ? THEME.primary : THEME.textSub }} />
                <select 
                  value={datePreset === 'custom' ? 'custom' : ''} 
                  onChange={e => setDatePreset(e.target.value)} 
                  style={{ border: 'none', background: 'transparent', outline: 'none', color: datePreset === 'custom' ? THEME.primary : THEME.textSub, fontWeight: '800', fontSize: '13px', cursor: 'pointer' }}
                >
                  <option value="" disabled>Custom</option>
                  <option value="custom">Select Range...</option>
                </select>
            </div>
          </div>
        </div>
      </div>

      {/* --- SMART MESH-GRADIENT KPI GRID --- */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
        gap: isMobile ? '16px' : '24px', 
        marginBottom: isMobile ? '24px' : '40px' 
      }}>
        
        {/* REVENUE CARD */}
        <div className="colorful-card animate-up" style={{ '--hover-glow': THEME.glowRevenue, background: THEME.gradRevenue, padding: isMobile ? '24px' : '30px', gridColumn: isMobile ? 'span 2' : 'span 1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
            <div style={{ background: 'rgba(255,255,255,0.25)', width: '48px', height: '48px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)' }}><FaWallet size={20}/></div>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', backdropFilter: 'blur(5px)' }}><FaArrowUp size={10} style={{marginRight:'4px'}}/> 15%</span>
          </div>
          <div style={{ marginTop: '30px', position: 'relative', zIndex: 2 }}>
            <h3 style={{ margin: 0, fontSize: isMobile ? '38px' : '44px', fontWeight: '900', letterSpacing: '-1.5px' }}>₹{metrics.estRevenue.toLocaleString()}</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '600', opacity: 0.9 }}>Projected Revenue</p>
            <p style={{ margin: '12px 0 0 0', fontSize: '12px', fontWeight: '500', opacity: 0.8, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '12px' }}>Based on ~{Math.floor(metrics.newLeads * 0.15)} spa conversions</p>
          </div>
          <FaChartLine style={{ position: 'absolute', right: -20, bottom: -20, fontSize: '140px', opacity: 0.1, zIndex: 1 }} />
        </div>

        {/* LEADS CARD */}
        <div className="colorful-card animate-up delay-1" style={{ '--hover-glow': THEME.glowLeads, background: THEME.gradLeads, padding: isMobile ? '20px' : '30px', gridColumn: 'span 1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
            <div style={{ background: 'rgba(255,255,255,0.2)', width: '42px', height: '42px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(5px)' }}><FaUserFriends size={18}/></div>
          </div>
          <div style={{ marginTop: '25px', position: 'relative', zIndex: 2 }}>
            <h3 style={{ margin: 0, fontSize: isMobile ? '32px' : '40px', fontWeight: '900', letterSpacing: '-1px' }}>{metrics.newLeads}</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', fontWeight: '600', opacity: 0.9 }}>New Leads Generated</p>
            <p style={{ margin: '10px 0 0 0', fontSize: '12px', opacity: 0.8, fontWeight: '500' }}>CPA: ₹{metrics.costPerLead.toFixed(2)}</p>
          </div>
        </div>

        {/* OPEN RATE CARD */}
        <div className="colorful-card animate-up delay-2" style={{ '--hover-glow': THEME.glowOpen, background: THEME.gradOpen, padding: isMobile ? '20px' : '30px', gridColumn: 'span 1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
            <div style={{ background: 'rgba(255,255,255,0.2)', width: '42px', height: '42px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(5px)' }}><FaEye size={18}/></div>
          </div>
          <div style={{ marginTop: '25px', position: 'relative', zIndex: 2 }}>
            <h3 style={{ margin: 0, fontSize: isMobile ? '32px' : '40px', fontWeight: '900', letterSpacing: '-1px' }}>{metrics.readRate}%</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', fontWeight: '600', opacity: 0.9 }}>Avg. Open Rate</p>
            <p style={{ margin: '10px 0 0 0', fontSize: '12px', opacity: 0.8, fontWeight: '500' }}>{metrics.totalRead} Total Reads</p>
          </div>
        </div>
        
        {/* HEALTH SCORE CARD */}
        <div className="colorful-card animate-up delay-3" style={{ '--hover-glow': THEME.glowHealth, background: THEME.gradHealth, padding: isMobile ? '24px' : '30px', gridColumn: isMobile ? 'span 2' : 'span 1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 2 }}>
             <p style={{ margin: 0, fontSize: '12px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Health Score</p>
             <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '800', background: metrics.healthScore > 70 ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', color: metrics.healthScore > 70 ? '#34d399' : '#fbbf24', padding: '6px 10px', borderRadius: '20px', border: metrics.healthScore > 70 ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(245,158,11,0.3)' }}>
                 {metrics.healthScore > 70 ? <FaCheckCircle size={12}/> : <FaExclamationCircle size={12}/>} {metrics.healthScore > 70 ? 'Healthy' : 'Warning'}
             </div>
          </div>
          <div style={{ marginTop: '20px', display: 'flex', alignItems: 'baseline', gap: '8px', position: 'relative', zIndex: 2 }}>
            <h3 style={{ margin: 0, fontSize: '48px', fontWeight: '900', letterSpacing: '-2px' }}>{metrics.healthScore}</h3>
            <span style={{ fontSize: '18px', color: '#64748b', fontWeight: '800' }}>/ 100</span>
          </div>
          <div style={{ marginTop: '20px', height: '8px', width: '100%', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', overflow: 'hidden', position: 'relative', zIndex: 2, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)' }}>
            <div style={{ height: '100%', width: `${metrics.healthScore}%`, background: metrics.healthScore > 70 ? '#34d399' : '#fbbf24', borderRadius: '4px', transition: 'width 1.5s cubic-bezier(0.16, 1, 0.3, 1)' }}></div>
          </div>
        </div>
      </div>

      {/* --- ANALYTICS ROW --- */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: isMobile ? '16px' : '24px', marginBottom: isMobile ? '16px' : '40px' }}>
        
        {/* MAIN AREA CHART */}
        <div className="white-card animate-up delay-2" style={{ padding: isMobile ? '25px 20px' : '35px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', padding: '0 10px' }}>
              <div>
                  <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '900', color: THEME.primary, letterSpacing: '-0.5px' }}>Engagement Trends</h3>
              </div>
              <div style={{ display: 'flex', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '800', color: THEME.textSub }}><div style={{width:'10px', height:'10px', borderRadius:'3px', background:THEME.chartSent}}></div> Sent</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '800', color: THEME.textSub }}><div style={{width:'10px', height:'10px', borderRadius:'3px', background:THEME.chartRead}}></div> Read</div>
              </div>
          </div>
          
          <div style={{ height: isMobile ? '240px' : '360px', width: '100%' }}>
            <ResponsiveContainer>
              <AreaChart data={engagementData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={THEME.chartSent} stopOpacity={0.3}/><stop offset="95%" stopColor={THEME.chartSent} stopOpacity={0}/></linearGradient>
                  <linearGradient id="colorRead" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={THEME.chartRead} stopOpacity={0.3}/><stop offset="95%" stopColor={THEME.chartRead} stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={THEME.border} />
                <XAxis dataKey="name" tick={{fontSize: 12, fill: THEME.textSub, fontWeight: 700}} axisLine={false} tickLine={false} dy={15} />
                <YAxis tick={{fontSize: 12, fill: THEME.textSub, fontWeight: 700}} axisLine={false} tickLine={false} />
                <RechartsTooltip content={<CustomTooltip />} cursor={{ stroke: THEME.border, strokeWidth: 2, strokeDasharray: '4 4' }} />
                <Area type="monotone" dataKey="Sent" stroke={THEME.chartSent} fillOpacity={1} fill="url(#colorSent)" strokeWidth={4} activeDot={{ r: 6, strokeWidth: 0, fill: THEME.chartSent, boxShadow: '0 0 10px rgba(99,102,241,0.5)' }} />
                <Area type="monotone" dataKey="Read" stroke={THEME.chartRead} fillOpacity={1} fill="url(#colorRead)" strokeWidth={4} activeDot={{ r: 6, strokeWidth: 0, fill: THEME.chartRead, boxShadow: '0 0 10px rgba(16,185,129,0.5)' }}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* FUNNEL CHART */}
        <div className="white-card animate-up delay-2" style={{ padding: isMobile ? '25px' : '35px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '100%', textAlign: 'left', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '900', color: THEME.primary, letterSpacing: '-0.5px' }}>Delivery Funnel</h3>
          </div>
          
          <div style={{ height: isMobile ? '220px' : '260px', width: '100%' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={funnelData} innerRadius={isMobile ? 60 : 75} outerRadius={isMobile ? 80 : 100} paddingAngle={5} dataKey="value" stroke="none">
                  <Cell fill={THEME.chartRead} />      
                  <Cell fill={THEME.chartDelivered} />      
                  <Cell fill={THEME.border} />            
                </Pie>
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend verticalAlign="bottom" height={40} iconType="circle" wrapperStyle={{ fontSize: '13px', fontWeight: '800', color: THEME.textSub }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div style={{ textAlign: 'center', background: THEME.bg, width: '100%', padding: '20px', borderRadius: '16px', marginTop: 'auto', border: `1px solid ${THEME.border}` }}>
            <p style={{ margin: 0, fontSize: '12px', color: THEME.textSub, fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Processed</p>
            <h2 style={{ margin: '4px 0 0 0', fontSize: '28px', fontWeight: '900', color: THEME.primary }}>{metrics.totalSent.toLocaleString()}</h2>
          </div>
        </div>
      </div>

      {/* --- BOTTOM ROW --- */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '16px' : '24px', paddingBottom: isMobile ? '40px' : '20px' }}>
        
        {/* ACTIVITY FEED */}
        <div className="white-card animate-up delay-3" style={{ padding: isMobile ? '25px 20px' : '35px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '900', color: THEME.primary, letterSpacing: '-0.5px' }}>Live Action Feed</h3>
          </div>
          
          <div className="custom-scroll" style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', height: '300px', paddingRight: '10px' }}>
            {recentActivity.length === 0 ? (
                <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', color:THEME.textSub}}>
                    <Icons.FaInbox size={40} style={{opacity: 0.15, marginBottom: '15px'}}/>
                    <p style={{margin:0, fontSize:'14px', fontWeight:'700'}}>Waiting for activity...</p>
                </div>
            ) : recentActivity.map((item, i) => (
              <div key={i} className="feed-item" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: `${item.color}15`, color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {item.icon}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '800', color: THEME.primary }}>{item.title}</p>
                  <p style={{ margin: 0, fontSize: '12px', color: THEME.textSub, fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaClock size={10} style={{opacity: 0.6}}/>
                      {item.time.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* LEAD GROWTH BAR CHART */}
        <div className="white-card animate-up delay-3" style={{ padding: isMobile ? '25px 20px' : '35px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', padding: '0 10px' }}>
              <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '900', color: THEME.primary, letterSpacing: '-0.5px' }}>Audience Growth</h3>
          </div>
          <div style={{ height: '300px', width: '100%' }}>
            <ResponsiveContainer>
              <BarChart data={leadData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={THEME.border} />
                <XAxis dataKey="date" tick={{fontSize: 12, fill: THEME.textSub, fontWeight: 700}} axisLine={false} tickLine={false} dy={15} />
                <YAxis tick={{fontSize: 12, fill: THEME.textSub, fontWeight: 700}} axisLine={false} tickLine={false} />
                <RechartsTooltip cursor={{fill: THEME.bg, radius: 8}} content={<CustomTooltip />} />
                <Bar dataKey="Leads" fill={THEME.primary} radius={[6, 6, 6, 6]} barSize={isMobile ? 20 : 32}>
                    {leadData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === leadData.length - 1 ? THEME.chartSent : THEME.primary} />
                    ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Dashboard;